import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import Toast from '../../../components/Common/Toast';
import { getPointOfSales } from '../../../services/pointOfSalesService';
import { ventasReportService } from '../../../services/ventasReportService';
import { formatDateLocaleLima } from '../../../utils/dateUtils';
import {
  VENTAS_DOCUMENT_TYPES,
  VENTAS_PAYMENT_METHODS,
} from '../../../utils/ventasSalesConstants';

const STORAGE_KEY = 'ventas_reports_sales_register_filters';

function defaultDates() {
  const to = new Date();
  const from = new Date(to.getFullYear(), to.getMonth(), 1);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { dateFrom: fmt(from), dateTo: fmt(to) };
}

function loadStoredFilters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {
        ...defaultDates(),
        pointOfSaleId: '',
        documentType: '',
        paymentMethod: '',
        status: 'issued',
        search: '',
      };
    }
    const parsed = JSON.parse(raw);
    const defaults = defaultDates();
    return {
      dateFrom: parsed.dateFrom || defaults.dateFrom,
      dateTo: parsed.dateTo || defaults.dateTo,
      pointOfSaleId: parsed.pointOfSaleId || '',
      documentType: parsed.documentType || '',
      paymentMethod: parsed.paymentMethod || '',
      status: parsed.status || 'issued',
      search: parsed.search || '',
    };
  } catch {
    return {
      ...defaultDates(),
      pointOfSaleId: '',
      documentType: '',
      paymentMethod: '',
      status: 'issued',
      search: '',
    };
  }
}

function saveStoredFilters(filters) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore
  }
}

const formatMoney = (amount) =>
  `S/ ${Number(amount || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function VentasSalesRegister() {
  const [filters, setFilters] = useState(loadStoredFilters);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    getPointOfSales()
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setPointsOfSale(res.data.filter((p) => p.is_active !== false));
        }
      })
      .catch(() => {});
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      saveStoredFilters(next);
      return next;
    });
  }, []);

  const buildParams = useCallback(() => {
    const params = {
      date_from: filters.dateFrom || undefined,
      date_to: filters.dateTo || undefined,
      status: filters.status || undefined,
    };
    if (filters.pointOfSaleId) params.point_of_sale_id = filters.pointOfSaleId;
    if (filters.documentType) params.document_type = filters.documentType;
    if (filters.paymentMethod) params.payment_method = filters.paymentMethod;
    if (filters.search.trim()) params.search = filters.search.trim();
    return params;
  }, [filters]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ventasReportService.getSalesRegister(buildParams());
      if (res.success) {
        setRows(res.data?.rows || []);
        setTotals(res.data?.totals || null);
        setHasQueried(true);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar el reporte', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el reporte de ventas',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportToExcel = async () => {
    if (!rows.length) {
      setToast({ isOpen: true, message: 'No hay datos para exportar', type: 'warning' });
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Reporte de ventas');

    sheet.mergeCells('A1:O1');
    sheet.getCell('A1').value = `Reporte de ventas (cabeceras) — ${filters.dateFrom} a ${filters.dateTo}`;
    sheet.getCell('A1').font = { bold: true, size: 14 };

    sheet.addRow([]);
    const headerRow = sheet.addRow([
      'N°',
      'Código',
      'Tipo',
      'Serie-Número',
      'Fecha',
      'Cliente',
      'Doc. cliente',
      'Punto de venta',
      'Medio de pago',
      'N° operación',
      'Base imponible',
      'IGV',
      'No gravados',
      'Total',
      'Estado',
    ]);
    headerRow.font = { bold: true };

    rows.forEach((row) => {
      sheet.addRow([
        row.row_number,
        row.code,
        row.document_type_label,
        row.series_number || '',
        row.issue_date ? formatDateLocaleLima(row.issue_date) : '',
        row.client_name || '',
        row.client_document || '',
        row.point_of_sale_name || row.point_of_sale_code || '',
        row.payment_method_label || '',
        row.operation_number || '',
        row.base_imponible,
        row.igv_amount,
        row.no_gravados,
        row.total_amount,
        row.status_label || '',
      ]);
    });

    if (totals) {
      sheet.addRow([]);
      sheet.addRow([
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        'Totales (solo emitidas)',
        totals.base_imponible,
        totals.igv_amount,
        totals.no_gravados,
        totals.total_amount,
        `${totals.issued_count || 0} emitida(s)`,
      ]);
    }

    sheet.columns.forEach((col) => {
      col.width = 14;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reporte-ventas-${filters.dateFrom}_${filters.dateTo}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-[1500px] mx-auto">
          <nav className="text-sm text-gray-600 mb-3">
            <Link to="/dashboard" className="hover:text-teal-600">
              Dashboard
            </Link>
            <span className="mx-2">/</span>
            <Link to="/ventas/orders" className="hover:text-teal-600">
              Ventas
            </Link>
            <span className="mx-2">/</span>
            <Link to="/ventas/reports" className="hover:text-teal-600">
              Reportes
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Reporte de ventas</span>
          </nav>

          <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Reporte de ventas</h1>
              <p className="text-gray-600 mt-1">
                Listado convencional por cabecera de venta (sin detalle de ítems). Los montos solo suman
                emitidas.
              </p>
            </div>
            <button
              type="button"
              onClick={exportToExcel}
              disabled={!rows.length}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
            >
              Exportar Excel
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-5 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Punto de venta</label>
                <select
                  value={filters.pointOfSaleId}
                  onChange={(e) => updateFilter('pointOfSaleId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Todos</option>
                  {pointsOfSale.map((pos) => (
                    <option key={pos.id} value={pos.id}>
                      {pos.code ? `${pos.code} — ${pos.name}` : pos.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Comprobante</label>
                <select
                  value={filters.documentType}
                  onChange={(e) => updateFilter('documentType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Todos</option>
                  {VENTAS_DOCUMENT_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Medio de pago</label>
                <select
                  value={filters.paymentMethod}
                  onChange={(e) => updateFilter('paymentMethod', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Todos</option>
                  {VENTAS_PAYMENT_METHODS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Estado</label>
                <select
                  value={filters.status}
                  onChange={(e) => updateFilter('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                >
                  <option value="issued">Emitidas</option>
                  <option value="cancelled">Canceladas</option>
                  <option value="all">Todos los estados</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[220px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Buscar</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  placeholder="Código, serie, cliente, operación…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') loadReport();
                  }}
                />
              </div>
              <button
                type="button"
                onClick={loadReport}
                disabled={loading}
                className="px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
              >
                {loading ? 'Consultando…' : 'Consultar'}
              </button>
            </div>
          </div>

          {totals ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Registros</div>
                <div className="text-2xl font-bold text-gray-800">{totals.orders_count || 0}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Emitidas</div>
                <div className="text-2xl font-bold text-teal-600">{totals.issued_count || 0}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Base imponible</div>
                <div className="text-xl font-bold text-gray-800">{formatMoney(totals.base_imponible)}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Monto total (emitidas)</div>
                <div className="text-xl font-bold text-blue-600">{formatMoney(totals.total_amount)}</div>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="text-center py-12 text-gray-500">Cargando reporte…</div>
          ) : !hasQueried ? null : rows.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">No hay ventas para los filtros seleccionados</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">N°</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comprobante</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">POS</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pago</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Base</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">IGV</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm text-gray-500">{row.row_number}</td>
                        <td className="px-3 py-3 text-sm font-mono text-teal-700">
                          <Link to={`/ventas/orders/${row.id}/edit`} className="hover:underline">
                            {row.code}
                          </Link>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-700">
                          <div>{row.document_type_label}</div>
                          {row.series_number ? (
                            <div className="text-xs font-mono text-gray-500">{row.series_number}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {row.issue_date ? formatDateLocaleLima(row.issue_date) : '—'}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-800">
                          <div className="font-medium">{row.client_name || '—'}</div>
                          {row.client_document ? (
                            <div className="text-xs text-gray-500">{row.client_document}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600">
                          {row.point_of_sale_name || row.point_of_sale_code || '—'}
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600">
                          <div>{row.payment_method_label || '—'}</div>
                          {row.operation_number ? (
                            <div className="text-xs font-mono text-gray-500">{row.operation_number}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-700 whitespace-nowrap">
                          {formatMoney(row.base_imponible)}
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-700 whitespace-nowrap">
                          {formatMoney(row.igv_amount)}
                        </td>
                        <td className="px-3 py-3 text-sm text-right font-medium text-gray-900 whitespace-nowrap">
                          {formatMoney(row.total_amount)}
                        </td>
                        <td className="px-3 py-3 text-sm">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              row.status === 'cancelled'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {row.status_label}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
}
